import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Typography,
} from '@mui/material';
import NumberInput from '../../../../../common/components/UI/Input/Number';
import RadioButton from '../../../../../common/components/UI/RadioGroup/RadioButton';
import DualListBox from '../../../../../common/components/UI/DualListBox/DualListBox';
import { UPDATE_VALIDATION_RULE } from '../../../store/ruleSet';
import { RequestTypes, TimeBeforeShiftType, SwapPeriodType } from '../../../../Enums';
import { hasAllowOverlapRule, hasTimeBeforeOrAfterShiftStartAllowedRule, hasMultipleShiftOnSameDayRule } from './Step4-ShiftRulesSection.service';
import { useTranslation } from 'react-i18next';
import StyledSwitch from '../StyledSwitch';
import ReactSelect from '../../../../../common/components/UI/Select/ReactSelect';
import './Step4-ShiftRulesSection.scss';
import _ from 'lodash';

const defaultMultipleShiftOnSameDayParameters = {
    maxMultipleShiftConsecutiveDay: 0,
    maxMultipleShiftPerWeek: 0,
}
/**
 * @param {{validationErrors: {[Key: string], isDisabled: boolean}, stepNumber: number }} props
 */
export default function ShiftRulesSection(props) {
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet' });
    const { t: tStep } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.shiftRulesStep' });

    const { validationErrors, isDisabled, stepNumber } = props;
    const dispatch = useDispatch();
    /** @type {RuleSet.IRuleSetDetails} */
    const adminRuleSet = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminRuleSet);
    const { validationRuleSet, requestType, requestSubtype } = adminRuleSet;
    const {
        isAllowShiftOverlap,
        overlapLength,
        isValidatePaycodeOverlap,
        paycodeOverlapLength,
        overlapPaycodeListToValidate,
        isValidateSameDayPaycode,
        sameDayPaycodeListToValidate,
        swapPeriodType,
        isOnlySwapOnSameLength,
        isTimeBeforeOrAfterShiftStartAllowed,
        timeBeforeShiftLength,
        timeBeforeShiftType,
        isTimeAfterShift,
        isMultipleShiftOnSameDayRuleEnabled,
        maxMultipleShiftConsecutiveDay,
        maxMultipleShiftPerWeek,
    } = validationRuleSet;

    /**
     * @param {string} target
     * @param {string} value
     */
    const updateValidationRulesStore = (target, value) => {
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                [target]: value,
            },
        });
    };

    /**
     * 
     * @param {boolean} isEnabled 
     */
    const updateMultipleShiftOnSameDayRuleEnabled = (isEnabled) => {
        const defaultParams = isEnabled ? _.cloneDeep(defaultMultipleShiftOnSameDayParameters) : null;
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                isMultipleShiftOnSameDayRuleEnabled: isEnabled,
                ...defaultParams,
            },
        });
    }

    const updateAllowedShiftOverlap = (value) => {
        let valuesToReset = null;
        if (!value) {
            valuesToReset = {
                overlapLength: null
            }
        }
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                isAllowShiftOverlap: value,
                ...valuesToReset,
            },
        });
    }

    const updateAllowedPaycodeOverlap = (value) => {
        let valuesToReset = null;
        if (!value) {
            moveAllOverlapPaycodeList('overlapPaycodeListToValidate', 'toLeft');
            valuesToReset = {
                paycodeOverlapLength: null
            }
        }
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                isValidatePaycodeOverlap: value,
                ...valuesToReset,
            },
        });
    }

    const updateSameDayPaycodes = (value) => {
        if (!value) {
            moveAllSameDayPaycodeList('sameDayPaycodeListToValidate', 'toLeft');
        }
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                isValidateSameDayPaycode: value,
            },
        });
    }



    const updateRestrictSwapPeriodSwitch = (isEnabled) => {
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                // Default to the most restrictive window when the rule is turned on.
                swapPeriodType: isEnabled ? SwapPeriodType.SameDay : null,
            },
        });
    }

    const updateTimeBeforeShiftSwitch = (value) => {
        let valuesToReset = null;
        if (!value) {
            valuesToReset = {
                timeBeforeShiftLength: 0,
                timeBeforeShiftType: TimeBeforeShiftType.Hours,
                isTimeAfterShift: false,
            }
        }
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                isTimeBeforeOrAfterShiftStartAllowed: value,
                ...valuesToReset,
            },
        });
    }

    /**
     *
     * @param {string} radioButtonValue
     */
    const updateTimeBeforeShiftType = (radioButtonValue) => {
        const updatedValues = {
            timeBeforeShiftType: parseInt(radioButtonValue),
            timeBeforeShiftLength: 0,
        }
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                ...updatedValues,
            },
        });
    }

    const handleOverlapPaycodeListChange = (selected, position) => {
        const { included, excluded } = overlapPaycodeListToValidate;

        if (position === "excluded") {
            const elementIndex = excluded.findIndex(x => x.value === selected.value);
            const element = excluded[elementIndex];
            excluded.splice(elementIndex, 1);
            included.push(element);
        } else if (position === "included") {
            const elementIndex = included.findIndex(x => x.value === selected.value);
            const element = included[elementIndex];
            included.splice(elementIndex, 1);
            excluded.push(element);
        }

        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                overlapPaycodeListToValidate: {
                    included: included,
                    excluded: excluded,
                }
            },
        });
    }

    const moveAllOverlapPaycodeList = (target, direction) => {
        const { included, excluded } = overlapPaycodeListToValidate;
        if (direction === "toRight") {
            included.push(...excluded);
            excluded.splice(0, excluded.length);
        } else if (direction === "toLeft") {
            excluded.push(...included);
            included.splice(0, included.length);
        }

        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                overlapPaycodeListToValidate: {
                    included: included,
                    excluded: excluded,
                }
            },
        });
    }

    const handleSameDayPaycodeListChange = (selected, position) => {
        const { included, excluded } = sameDayPaycodeListToValidate;

        if (position === "excluded") {
            const elementIndex = excluded.findIndex(x => x.value === selected.value);
            const element = excluded[elementIndex];
            excluded.splice(elementIndex, 1);
            included.push(element);
        } else if (position === "included") {
            const elementIndex = included.findIndex(x => x.value === selected.value);
            const element = included[elementIndex];
            included.splice(elementIndex, 1);
            excluded.push(element);
        }

        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                sameDayPaycodeListToValidate: {
                    included: included,
                    excluded: excluded,
                }
            },
        });
    }

    const moveAllSameDayPaycodeList = (target, direction) => {
        const { included, excluded } = sameDayPaycodeListToValidate;
        if (direction === "toRight") {
            included.push(...excluded);
            excluded.splice(0, excluded.length);
        } else if (direction === "toLeft") {
            excluded.push(...included);
            included.splice(0, included.length);
        }

        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                sameDayPaycodeListToValidate: {
                    included: included,
                    excluded: excluded,
                }
            },
        });
    }

    const isVto = requestType === RequestTypes.voluntaryTimeOff;
    const isShiftSwap = requestType === RequestTypes.shiftSwap;
    const swapPeriodOptions = [
        { value: SwapPeriodType.SameDay, label: tStep('swapPeriod.sameDay') },
        { value: SwapPeriodType.SameWeek, label: tStep('swapPeriod.sameWeek') },
        { value: SwapPeriodType.SamePayPeriod, label: tStep('swapPeriod.samePayPeriod') },
        { value: SwapPeriodType.SameSchedulePeriod, label: tStep('swapPeriod.sameSchedulePeriod') },
    ];
    return (
        <Card className="card">
            <CardHeader
                className="card--header"
                titleTypographyProps={{ gutterBottom: true }}
                avatar={<Avatar className="avatar">{stepNumber}</Avatar>}
                title={tStep('title')}
            />
            <CardContent className="no-gap">
                {
                    hasAllowOverlapRule(requestType) && (<>
                        <div>
                            <StyledSwitch
                                checked={isAllowShiftOverlap}
                                onChange={(value) => updateAllowedShiftOverlap(value)}
                                label={tStep('validateShiftOverlap')}
                                style={{ display: 'inline-flex' }}
                                disabled={isDisabled}
                            />
                            { isAllowShiftOverlap && (
                                <>
                                    <NumberInput
                                        handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                                        min={0}
                                        max={999}
                                        step={1}
                                        precision={0}
                                        value={overlapLength}
                                        defaultValue={1}
                                        target={'overlapLength'}
                                        isDisabled={isDisabled}
                                        strict={true}
                                        error={validationErrors.overlapLength}
                                    />
                                    <Typography
                                        variant="body1"
                                        className="srs-overlap-length-unit-label"
                                    >
                                        {t('timeUnits.minutes')}
                                    </Typography>
                                </>)}
                            {
                                validationErrors.overlapLength
                                    ? <span style={{ color: 'red', display: 'block' }}>
                                        {tStep('validateShiftOverlapLengthError')}
                                    </span>
                                    : null
                            }
                        </div>
                        <div>
                            <StyledSwitch
                                checked={isValidatePaycodeOverlap}
                                onChange={(value) => updateAllowedPaycodeOverlap(value)}
                                label={tStep('validatePaycodeOverlap')}
                                tooltip={tStep('validatePaycodeOverlapTooltip')}
                                style={{ display: 'inline-flex' }}
                                disabled={isDisabled}
                            />
                            {
                                isValidatePaycodeOverlap && (
                                    <>
                                         <>
                                        <NumberInput
                                            handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                                            min={0}
                                            max={999}
                                            step={1}
                                            precision={0}
                                            value={paycodeOverlapLength}
                                            defaultValue={1}
                                            target={'paycodeOverlapLength'}
                                            isDisabled={isDisabled}
                                            strict={true}
                                            error={validationErrors.paycodeOverlapLength}
                                        />
                                        <Typography
                                            variant="body1"
                                            className="srs-overlap-length-unit-label"
                                        >
                                            {t('timeUnits.minutes')}
                                        </Typography>
                                        {
                                            validationErrors.paycodeOverlapLength
                                                ? <span style={{ color: 'red', display: 'block' }}>
                                                    {tStep('validatePaycodeOverlapLengthError')}
                                                </span>
                                                : null
                                        }
                                    </>
                                        <DualListBox
                                            data={overlapPaycodeListToValidate.included}
                                            subHeader={'Paycodes'}
                                            rightBoxText={'Selected'}
                                            leftBoxText={'Unselected'}
                                            moveAllToRightText={'Select all'}
                                            moveAllToLeftText={'Unselect all'}
                                            target={'overlapPaycodeListToValidate'}
                                            dataSelected={overlapPaycodeListToValidate.excluded}
                                            handleChangeDualBox={handleOverlapPaycodeListChange}
                                            moveAll={moveAllOverlapPaycodeList}
                                        />
                                    </>)
                            }
                            {
                                validationErrors.noPaycodesSelected
                                    ? <span style={{ color: 'red', display: 'block' }}>
                                        {tStep('validateNoPaycodeSelectedError')}
                                    </span>
                                    : null
                            }
                        </div>
                        <div>
                            <StyledSwitch
                                checked={isValidateSameDayPaycode}
                                onChange={(value) => updateSameDayPaycodes(value)}
                                label={tStep('validateSameDayPaycode')}
                                tooltip={tStep('validateSameDayPaycodeTooltip')}
                                style={{ display: 'inline-flex' }}
                                disabled={isDisabled}
                            />
                            {
                                isValidateSameDayPaycode && (
                                    <>
                                        <DualListBox
                                            data={sameDayPaycodeListToValidate.included}
                                            subHeader={'Paycodes'}
                                            rightBoxText={'Selected'}
                                            leftBoxText={'Unselected'}
                                            moveAllToRightText={'Select all'}
                                            moveAllToLeftText={'Unselect all'}
                                            target={'sameDayPaycodeListToValidate'}
                                            dataSelected={sameDayPaycodeListToValidate.excluded}
                                            handleChangeDualBox={handleSameDayPaycodeListChange}
                                            moveAll={moveAllSameDayPaycodeList}
                                        />
                                    </>)
                            }
                            {
                                validationErrors.noSameDayPaycodesSelected
                                    ? <span style={{ color: 'red', display: 'block' }}>
                                        {tStep('validateNoPaycodeSelectedError')}
                                    </span>
                                    : null
                            }
                        </div>
                    </>)
                }
                {
                    isShiftSwap && (
                        <div>
                            <StyledSwitch
                                checked={swapPeriodType !== null && swapPeriodType !== undefined}
                                onChange={(value) => updateRestrictSwapPeriodSwitch(value)}
                                label={tStep('restrictSwapPeriod')}
                                disabled={isDisabled}
                            />
                            {(swapPeriodType !== null && swapPeriodType !== undefined) && (
                                <ReactSelect
                                    options={swapPeriodOptions}
                                    value={swapPeriodOptions.find(x => x.value === swapPeriodType)}
                                    onChange={(target, selectedValue) => updateValidationRulesStore('swapPeriodType', selectedValue.value)}
                                    isDisabled={isDisabled}
                                />
                            )}
                            <StyledSwitch
                                checked={isOnlySwapOnSameLength}
                                onChange={(value) => updateValidationRulesStore('isOnlySwapOnSameLength', value)}
                                label={tStep('onlyShiftsSwappedWithTheSameLengthAccepeted')}
                                disabled={isDisabled}
                            />
                        </div>)
                }
                {
                    hasTimeBeforeOrAfterShiftStartAllowedRule(requestType, requestSubtype) && (
                        <div className="time-before-shift-rules-section">
                            <StyledSwitch
                                checked={isTimeBeforeOrAfterShiftStartAllowed}
                                onChange={(value) => updateTimeBeforeShiftSwitch(value)}
                                label={isVto ? tStep('timeBeforeOrAfterTimeOffStartAllowed') : tStep('timeBeforeOrAfterShiftStartAllowed')}
                                disabled={isDisabled}
                            />
                            {isTimeBeforeOrAfterShiftStartAllowed && (
                                <div className='shift-type-fields-container'>
                                    <div className='shift-type-fields'>
                                        {timeBeforeShiftType === TimeBeforeShiftType.Hours
                                            ? (<>
                                                <NumberInput
                                                    handleChangeInput={(value, target) => {
                                                        const fractionOfHour = timeBeforeShiftLength % 1;
                                                        const totalHours = value + fractionOfHour;
                                                        updateValidationRulesStore(target, totalHours)
                                                    }}
                                                    min={0}
                                                    max={999}
                                                    step={1}
                                                    precision={0}
                                                    value={parseInt(timeBeforeShiftLength)}
                                                    isDisabled={isDisabled}
                                                    strict={true}
                                                    target='timeBeforeShiftLength'
                                                    error={validationErrors.timeBeforeShiftLength}
                                                    unitType={t('timeUnits.hours')}
                                                />
                                                <NumberInput
                                                    handleChangeInput={(value, target) => {
                                                        const hours = parseInt(timeBeforeShiftLength);
                                                        const totalHours = (value / 60) + hours;
                                                        updateValidationRulesStore(target, totalHours)
                                                    }}
                                                    min={0}
                                                    max={999}
                                                    step={1}
                                                    precision={0}
                                                    value={(timeBeforeShiftLength % 1) * 60}
                                                    isDisabled={isDisabled}
                                                    strict={true}
                                                    target='timeBeforeShiftLength'
                                                    error={validationErrors.timeBeforeShiftLength}
                                                    unitType={t('timeUnits.minutes')}
                                                />
                                            </>)
                                            : (
                                                <NumberInput
                                                    handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                                                    min={0}
                                                    max={999}
                                                    step={1}
                                                    precision={0}
                                                    value={timeBeforeShiftLength}
                                                    isDisabled={isDisabled}
                                                    strict={true}
                                                    target='timeBeforeShiftLength'
                                                    error={validationErrors.timeBeforeShiftLength}
                                                />)
                                        }
                                        <RadioButton
                                            radioGroupClass={'srs-timeUnitContainer'}
                                            valueRadioGroup={timeBeforeShiftType !== null ? timeBeforeShiftType.toString() : null}
                                            onChange={(event) => updateTimeBeforeShiftType(event.target.value)}
                                            labelA={t('timeUnits.hours')}
                                            valueA={TimeBeforeShiftType.Hours.toString()}
                                            labelB={t('timeUnits.days')}
                                            valueB={TimeBeforeShiftType.Days.toString()}
                                            isRadioADisabled={isDisabled || !isTimeBeforeOrAfterShiftStartAllowed}
                                            isRadioBDisabled={isDisabled || !isTimeBeforeOrAfterShiftStartAllowed}
                                        />
                                    </div>
                                    <RadioButton
                                        radioGroupClass={'time-after-shift-container'}
                                        valueRadioGroup={isTimeAfterShift ?? false}
                                        onChange={(event) => updateValidationRulesStore('isTimeAfterShift', event.target.value === 'true')}
                                        labelA={tStep('beforeShiftStarts')}
                                        valueA={false}
                                        labelB={tStep('afterShiftStarts')}
                                        valueB={true}
                                        isRadioADisabled={isDisabled || !isTimeBeforeOrAfterShiftStartAllowed}
                                        isRadioBDisabled={isDisabled || !isTimeBeforeOrAfterShiftStartAllowed}
                                    />
                                </div>)}
                            {
                                validationErrors.timeBeforeShiftLength
                                    ? <span style={{ color: 'red', display: 'block' }}>
                                        {tStep('timeBeforeOrAfterShiftStartAllowedLengthError')}
                                    </span>
                                    : null
                            }
                            {
                                validationErrors.timeBeforeShiftType
                                    ? <span style={{ color: 'red', display: 'block' }}>
                                        {tStep('timeBeforeOrAfterShiftStartAllowedUnitError')}
                                    </span>
                                    : null
                            }
                        </div>)
                }
                {
                    hasMultipleShiftOnSameDayRule(requestType) && (
                        <div className="double-shift-rules-section">
                            <StyledSwitch
                                checked={isMultipleShiftOnSameDayRuleEnabled}
                                onChange={(value) => updateMultipleShiftOnSameDayRuleEnabled(value)}
                                label={tStep('doubleShift')}
                                disabled={isDisabled}
                            />
                            {isMultipleShiftOnSameDayRuleEnabled && (
                                <>
                                    <div className="number-input">
                                        <NumberInput
                                            handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                                            min={0}
                                            max={7}
                                            step={1}
                                            precision={0}
                                            value={parseInt(maxMultipleShiftConsecutiveDay)}
                                            isDisabled={isDisabled}
                                            strict={true}
                                            target='maxMultipleShiftConsecutiveDay'
                                            error={validationErrors.maxMultipleShiftConsecutiveDay}
                                            key='maxMultipleShiftConsecutiveDay'
                                        />
                                        <Typography variant="body1">
                                            {tStep('maximumConsecutiveDays')}
                                        </Typography>

                                    </div>
                                    {
                                        validationErrors.maxMultipleShiftConsecutiveDay
                                            ? <span style={{ color: 'red', display: 'block' }}>
                                                {tStep('maximumConsecutiveDaysError')}
                                            </span>
                                            : null
                                    }
                                    <div className="number-input">
                                        <NumberInput
                                            handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                                            min={0}
                                            max={7}
                                            step={1}
                                            precision={0}
                                            value={parseInt(maxMultipleShiftPerWeek)}
                                            isDisabled={isDisabled}
                                            strict={true}
                                            target='maxMultipleShiftPerWeek'
                                            error={validationErrors.maxMultipleShiftPerWeek}
                                            key='maxMultipleShiftPerWeek'
                                        />
                                        <Typography variant="body1">
                                            {tStep('maximumPerWeeks')}
                                        </Typography>
                                    </div>
                                    {
                                        validationErrors.maxMultipleShiftPerWeek
                                            ? <span style={{ color: 'red', display: 'block' }}>
                                                {tStep('maximumPerWeeksError')}
                                            </span>
                                            : null
                                    }
                                </>)}
                        </div>
                    )
                }
            </CardContent>
        </Card>

    );
}
